export function getFriendlyErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case "auth/invalid-login-credentials":
      return "Invalid email or password. Please try again.";
    case "auth/invalid-phone-number":
      return "The phone number entered is invalid.";
    case "auth/missing-phone-number":
      return "Please enter a phone number.";
    case "auth/invalid-verification-code":
      return "The verification code is incorrect.";
    case "auth/code-expired":
      return "The verification code has expired. Please request a new one.";
    case "auth/too-many-attempts-try-later":
    case "auth/too-many-requests":
      return "Too many verification attempts. Please wait a few minutes before trying again.";
    // Add more cases as needed
    default:
      return "An unexpected error occurred. Please try again.";
  }
}

// --- After.com API Error Handling ---

// Define known API error codes with nested structure
export const ApiErrorCode = {
  Common: {
    INVALID_INPUT: "invalid_input",
    UNAUTHORIZED: "unauthorized",
    CONFIG_ERROR: "config_error",
    INTERNAL_SERVER_ERROR: "internal_server_error",
  },
  Arrangement: {
    // The person being billed has neither an email nor a phone on file, so no
    // card can be set up until someone adds one. Retrying cannot clear it.
    PERSON_UNREACHABLE: "person_unreachable",
    // The death certificates were already ordered, so who orders them can no
    // longer change. Recoverable in place: the step restores the case's own
    // values and turns the question read-only.
    DC_ORDERING_PREFERENCE_LOCKED: "dc_ordering_preference_locked",
    // The draft's death certificate choice disagrees with the case, so the
    // selections can't be confirmed against it.
    DC_ORDERING_PREFERENCE_MISMATCH: "dc_ordering_preference_mismatch",
  },
  PreNeed: {
    // The policy's no-money-down option refuses this payment method: its plan
    // draws payments from a bank account, or the policy no longer qualifies
    // for the option. A bank account or the agent resolves it.
    NO_MONEY_DOWN_INELIGIBLE: "no_money_down_ineligible",
    // Cal.com refused the call booking because the email's domain can't
    // receive mail. Recoverable in place by correcting the address.
    ATTENDEE_EMAIL_UNDELIVERABLE: "attendee_email_undeliverable",
    // The partial-down counterpart of NO_MONEY_DOWN_INELIGIBLE, with the same
    // causes and the same resolution.
    PARTIAL_DOWN_INELIGIBLE: "partial_down_ineligible",
  },
  Auth: {
    TOKEN_NOT_FOUND: "token_not_found",
    TOKEN_EXPIRED: "token_expired",
    TOKEN_INVALID: "token_invalid",
    TOKEN_INVALID_DETAILS: "token_invalid_details", // SMS specific
    VERIFICATION_NOT_FOUND: "verification_not_found",
    USER_LOOKUP_FAILED: "user_lookup_failed",
    PHONE_MISMATCH: "phone_mismatch", // Phone update specific
    INVALID_CREDENTIALS: "invalid_credentials", // Password login
    TOO_MANY_ATTEMPTS: "too_many_attempts",
  },
} as const; // Use const assertion for stronger typing

// Helper type to get all possible error code values
type Flatten<T> = T extends object
  ? { [K in keyof T]: Flatten<T[K]> }[keyof T]
  : T;
type ApiErrorCodeValue = Flatten<typeof ApiErrorCode>;

// A payment-option refusal can mean the plan draws from a bank account or
// that the policy no longer qualifies for the option; the code doesn't say
// which, so the copy covers both.
const PAYMENT_OPTION_REFUSAL_MESSAGE =
  "This payment method can't be used with your plan. Please enter a bank account, or contact your agent.";

// Mapping uses the correct lowercase snake_case values as keys
const apiErrorMessageMap: Record<ApiErrorCodeValue, string> = {
  // Auth Verify Errors
  [ApiErrorCode.Auth.TOKEN_NOT_FOUND]:
    "We couldn't find a match for this verification link. It might be incorrect, or you could try requesting a new one.",
  [ApiErrorCode.Auth.TOKEN_EXPIRED]:
    "This verification link has expired. Please return to the login page to request a new one.",
  [ApiErrorCode.Auth.TOKEN_INVALID]:
    "This verification link is no longer valid, perhaps it has already been used. Please try requesting a new one if needed.",
  [ApiErrorCode.Auth.TOKEN_INVALID_DETAILS]:
    "There was an issue verifying your information from the text message. Please try again or contact support.",
  [ApiErrorCode.Auth.VERIFICATION_NOT_FOUND]:
    "We couldn't find a pending verification matching this request. You may need to start the process again.",
  [ApiErrorCode.Auth.USER_LOOKUP_FAILED]:
    "We encountered a problem checking your account details. Please try again in a few moments.",
  [ApiErrorCode.Auth.PHONE_MISMATCH]:
    "The phone number provided doesn't match the one currently associated with your account. Please ensure you are logged in correctly.",
  // Auth Login (Password) Errors
  [ApiErrorCode.Auth.INVALID_CREDENTIALS]:
    "The email, phone number, or password you entered doesn't match our records. Please check your details and try again.",
  // Common Errors (apply across endpoints)
  [ApiErrorCode.Common.INVALID_INPUT]:
    "There seems to be an issue with the information provided. Please double-check and try again.",
  [ApiErrorCode.Common.UNAUTHORIZED]:
    "You are not authorized for this action. Please ensure you are logged in if required.",
  [ApiErrorCode.Common.CONFIG_ERROR]:
    "We are currently unable to process this request due to a technical issue. Please try again later or contact support.", // Slightly adjusted message
  [ApiErrorCode.Common.INTERNAL_SERVER_ERROR]:
    "We seem to be having trouble completing your request right now. Please try again in a few moments.",
  [ApiErrorCode.Auth.TOO_MANY_ATTEMPTS]:
    "Too many attempts. Please wait a few minutes before trying again.",
  // Arrangement Errors
  [ApiErrorCode.Arrangement.PERSON_UNREACHABLE]:
    "We need an email address or phone number on file before a card can be added. Your care specialist can add it for you.",
  [ApiErrorCode.Arrangement.DC_ORDERING_PREFERENCE_LOCKED]:
    "Death certificates have already been ordered for this case, so this choice can't be changed.",
  [ApiErrorCode.Arrangement.DC_ORDERING_PREFERENCE_MISMATCH]:
    "The death certificate details on this case have changed. Please take another look before confirming.",
  // Pre-Need Errors
  [ApiErrorCode.PreNeed.NO_MONEY_DOWN_INELIGIBLE]:
    PAYMENT_OPTION_REFUSAL_MESSAGE,
  [ApiErrorCode.PreNeed.ATTENDEE_EMAIL_UNDELIVERABLE]:
    "We couldn't send a confirmation to that email address. Please check it for a typo.",
  [ApiErrorCode.PreNeed.PARTIAL_DOWN_INELIGIBLE]:
    PAYMENT_OPTION_REFUSAL_MESSAGE,
};

/**
 * Error codes for a payment method the policy's payment option refuses (a
 * card where the plan draws from a bank account, or a policy that no longer
 * qualifies for the option). They are expected,
 * user-fixable refusals, so they are shown to the customer and not reported
 * as errors.
 */
const PAYMENT_OPTION_REFUSAL_CODES: string[] = [
  ApiErrorCode.PreNeed.NO_MONEY_DOWN_INELIGIBLE,
  ApiErrorCode.PreNeed.PARTIAL_DOWN_INELIGIBLE,
];

export function isPaymentOptionRefusal(
  errorCode: string | null | undefined,
): boolean {
  return !!errorCode && PAYMENT_OPTION_REFUSAL_CODES.includes(errorCode);
}

/**
 * Error codes no amount of retrying will clear — they need a person to change
 * something on our side. The UI offers a way to reach their care specialist
 * instead of a retry button.
 */
const TERMINAL_ERROR_CODES: string[] = [
  ApiErrorCode.Arrangement.PERSON_UNREACHABLE,
];

export function isTerminalApiError(
  errorCode: string | null | undefined,
): boolean {
  return !!errorCode && TERMINAL_ERROR_CODES.includes(errorCode);
}

/**
 * Pulls the `error_code` off a thrown NetworkError, whose own `message` is
 * empty — the parsed body is the only place the reason lives.
 *
 * Deliberately drops the body's `error` string: our APIs write it for whoever
 * holds the record ("Funeral home does not have a Stripe account configured"),
 * not for the family reading the screen. Route the code through
 * `getApiErrorMessage` and let unrecognized codes land on the caller's own
 * copy.
 */
export function apiErrorCode(error: unknown): string | undefined {
  return (error as { responseData?: { error_code?: string } })?.responseData
    ?.error_code;
}

/**
 * Gets a user-friendly error message based on an After.com API error code.
 *
 * @param errorCode The lowercase snake_case error code string from the API response.
 * @param defaultMessage An optional fallback message if the code is not recognized.
 * @returns A user-friendly error message string.
 */
export function getApiErrorMessage(
  errorCode: string | null | undefined,
  defaultMessage: string = "An unexpected error occurred. Please try again later.",
): string {
  // Use a safer check for property existence
  if (
    errorCode &&
    Object.prototype.hasOwnProperty.call(apiErrorMessageMap, errorCode)
  ) {
    // Assert type here as hasOwnProperty check confirms it's a key
    return apiErrorMessageMap[errorCode as ApiErrorCodeValue];
  }
  // Fallback for null, undefined, or unrecognized error codes
  return defaultMessage;
}

// Mapping for `GET /accounts/login/lookup` which returns HTTP status codes
// without a typed `error_code` body. Sibling of `getApiErrorMessage` above.
const lookupStatusMessageMap: Record<number, string> = {
  400: "Please enter a valid email address or phone number",
  404: "No account found with that email or phone number.",
  429: "Too many attempts. Please try again later.",
  500: "Something went wrong. Please try again.",
};

export function getLookupErrorMessage(
  statusCode: number | null | undefined,
  defaultMessage: string = "An unexpected error occurred. Please try again.",
): string {
  if (
    typeof statusCode === "number" &&
    Object.prototype.hasOwnProperty.call(lookupStatusMessageMap, statusCode)
  ) {
    return lookupStatusMessageMap[statusCode];
  }
  return defaultMessage;
}
